import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Code, Play, Sparkles, Loader2, ShieldAlert, X, Database } from 'lucide-react';

// Governed, read-only advanced-SQL fallback for warehouse power-questions.
// Admins only (backend also enforces). Generates SQL for review, then runs it
// through read-only + PII validation.
export const AdvancedSqlPanel = ({ modelId, initialQuestion = '', onClose }) => {
  const [sql, setSql] = useState('');
  const [nlq, setNlq] = useState(initialQuestion);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [warning, setWarning] = useState(null);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);

  const generate = async () => {
    if (!nlq.trim()) return;
    setGenerating(true); setError(null); setWarning(null);
    try {
      const res = await api.exploreSqlGenerate({ model_id: modelId, question: nlq });
      setSql(res.sql || '');
      if (!res.valid) setWarning(res.warning || 'Generated SQL needs review before running.');
    } catch (e) {
      setError(e?.message || 'Could not generate SQL.');
    } finally { setGenerating(false); }
  };

  const run = async () => {
    if (!sql.trim()) return;
    setRunning(true); setError(null); setRows(null); setMeta(null);
    try {
      const res = await api.exploreSqlExecute({ model_id: modelId, sql });
      setRows(res.data || []);
      setMeta({ rowCount: res.row_count, ms: res.duration_ms, via: res.executed_via });
    } catch (e) {
      setError(e?.message || 'Query failed.');
    } finally { setRunning(false); }
  };

  const cols = rows && rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <div className="w-full rounded-2xl border border-amber-500/30 bg-card shadow-2xl p-4 mb-6" data-testid="advanced-sql-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-300">
          <Code className="w-4 h-4" /> Advanced SQL
          <span className="text-[10px] font-normal text-muted-foreground border border-border/40 rounded-full px-2 py-0.5">read-only · admin</span>
        </div>
        {onClose && <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="advanced-sql-close"><X className="w-4 h-4" /></button>}
      </div>

      <div className="flex gap-2 mb-3">
        <input
          value={nlq}
          onChange={(e) => setNlq(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') generate(); }}
          placeholder="Describe the query in plain English, then Generate…"
          className="flex-1 bg-background/60 border border-border/40 rounded-lg px-3 py-2 text-xs text-foreground outline-none focus:border-primary/40"
          data-testid="advanced-sql-nl-input"
        />
        <Button size="sm" variant="outline" onClick={generate} disabled={generating || !nlq.trim()} data-testid="advanced-sql-generate-btn">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          <span className="ml-1.5">Generate</span>
        </Button>
      </div>

      <textarea
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        rows={6}
        spellCheck={false}
        placeholder="SELECT ... FROM ... — a single read-only SELECT. SELECT * and PII columns are blocked."
        className="w-full font-mono text-xs bg-background/70 border border-border/40 rounded-lg px-3 py-2 text-foreground outline-none focus:border-primary/40 resize-y"
        data-testid="advanced-sql-editor"
      />

      {warning && (
        <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-300" data-testid="advanced-sql-warning">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {warning}
        </div>
      )}
      {error && (
        <div className="mt-2 flex items-start gap-2 text-[11px] text-red-400" data-testid="advanced-sql-error">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <div className="flex items-center gap-3 mt-3">
        <Button size="sm" onClick={run} disabled={running || !sql.trim()} data-testid="advanced-sql-run-btn">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          <span className="ml-1.5">Run</span>
        </Button>
        {meta && <span className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Database className="w-3 h-3" />{meta.rowCount} rows · {meta.ms}ms · {meta.via}</span>}
      </div>

      {rows && (
        <div className="mt-3 max-h-72 overflow-auto rounded-lg border border-border/30" data-testid="advanced-sql-results">
          {rows.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground">No rows returned.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background/95">
                <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2 font-medium text-muted-foreground border-b border-border/30">{c}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((r, i) => (
                  <tr key={i} className="hover:bg-primary/5">
                    {cols.map((c) => <td key={c} className="px-3 py-1.5 text-foreground/80 border-b border-border/10">{r[c] === null || r[c] === undefined ? '—' : String(r[c])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
